import { createContext, useContext, useEffect, useReducer } from 'react'
import { captureException } from '@sentry/react'
import { inputValidators } from '@sellpy/commons'
import { DateTime } from 'luxon'
import isNil from 'lodash/isNil'
import omitBy from 'lodash/omitBy'
import { defaultAddressOptionalFields } from '../../../common/region/region'
import { useCurrentUserId } from '../../../common/user/currentUser'
import { useCurrentUser } from '../../../common/user/currentApolloUser'
import usePrevious from '../../hooks/usePrevious'

const CheckoutContext = createContext()

const initialize = ({ address, email, freight, promoCode, containerOrder }) => {
  const addressValidationErrors = inputValidators.address({
    address: address || {},
    optionalFields: [...defaultAddressOptionalFields(), 'email']
  })
  const isValidAddress = !addressValidationErrors.filter(Boolean).length
  const isValidEmail = !inputValidators.email(email)
  const initialState = {
    address: isValidAddress ? address : null,
    email: isValidEmail ? email : null,
    freight,
    promoCode,
    containerOrder
  }
  return omitBy(initialState, isNil)
}

const reducer = (state, { type, data }) => {
  switch (type) {
    case 'reInitialize': {
      const { address, email } = data
      return omitBy(
        {
          ...state,
          address: address || null,
          email: email || null
        },
        isNil
      )
    }
    case 'setEmail': {
      return { ...state, email: data }
    }
    case 'setAddress': {
      return { ...state, address: data }
    }
    case 'setPromoCode': {
      return { ...state, promoCode: data }
    }
    case 'setContainerOrder': {
      return { ...state, containerOrder: data }
    }
    case 'setFreightChoices': {
      return { ...state, freightChoices: data }
    }
    case 'setIsFastHandlingSelected': {
      return { ...state, isFastHandlingSelected: data }
    }
    default:
      return state
  }
}

export const CheckoutProvider = ({ children }) => {
  const user = useCurrentUser()
  const { getCachedCartContext, cacheCartContext } = useCartCacheContext()
  const [state, dispatch] = useReducer(
    reducer,
    getCachedCartContext() || { address: user?.address, email: user?.email },
    initialize
  )

  useEffect(() => {
    // Keeping the local storage cache in sync with local state
    if (user) {
      // we deliberately omit freightChoices
      const { freightChoices, ...stateWithoutFreight } = state
      cacheCartContext(stateWithoutFreight)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.email, state.address, state.promoCode, state.containerOrder])

  const previousUserId = usePrevious(user?.objectId)
  useEffect(() => {
    // Keeping local state in sync at login/logout
    if (user?.objectId !== previousUserId) {
      // Re-use state value only if signing in
      const email = previousUserId ? user?.email : state.email || user?.email
      const address = previousUserId ? user?.address : state.address || user?.address
      dispatch({
        type: 'reInitialize',
        data: { email, address }
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previousUserId, user])

  return (
    <CheckoutContext.Provider
      value={{
        state,
        dispatch
      }}
    >
      {children}
    </CheckoutContext.Provider>
  )
}

export const useCartContext = () => [
  useContext(CheckoutContext).state,
  useContext(CheckoutContext).dispatch
]

const getCacheKey = (userId) => `checkoutContextCache_${userId}`

export const clearCachedCartContext = (userId) => {
  window.localStorage.removeItem(getCacheKey(userId))
}

const CACHE_TTL_DURATION = { hour: 1 }

const getLocalStorageSizeInKiB = () => {
  // strings are stored in utf-16, so multiply by 2 at the end.
  const sizeInBytes =
    Object.entries(localStorage)
      .filter(([key]) => Object.hasOwn(localStorage, key))
      .reduce((acc, [key, value]) => acc + key.length + value.length, 0) * 2
  return (sizeInBytes / 1024).toFixed(2)
}
const useCartCacheContext = () => {
  const userId = useCurrentUserId()
  const cacheKey = getCacheKey(userId)
  const previousUser = usePrevious(userId)

  const cacheCartContext = (cartState, alreadyTried = false) => {
    const payload = JSON.stringify({ cartState, cachedAt: new Date() })
    try {
      window.localStorage.setItem(cacheKey, payload)
    } catch (e) {
      const quotaInformation =
        e?.quota && e.requested ? `\nRequested: ${e.requested}. Quota: ${e.quota}.` : ''
      captureException(
        new Error(`Could not set localStorage['${cacheKey}']!
Total local storage size: ${getLocalStorageSizeInKiB()} KiB.
Payload size: ${(2 * payload.length) / 1024} KiB.
${e}${quotaInformation}`)
      )
      if (!alreadyTried) {
        window.localStorage.removeItem(cacheKey)
        cacheCartContext(cartState, true)
      }
    }
  }

  const getCachedCartContext = () => {
    try {
      const cache = window.localStorage.getItem(cacheKey)

      if (!cache) return

      const { cartState, cachedAt } = JSON.parse(cache)

      const cacheIsExpired =
        DateTime.fromJSDate(new Date(cachedAt)).plus(CACHE_TTL_DURATION) < DateTime.local()

      if (cacheIsExpired) {
        clearCachedCartContext(userId)
        return
      }
      if (previousUser && previousUser !== userId) {
        clearCachedCartContext(previousUser)
        return
      }

      return cartState
    } catch {
      clearCachedCartContext(userId)
    }
  }

  return {
    cacheCartContext,
    getCachedCartContext
  }
}
